// The summary an observer sees during a session and when they end it: time and
// share per activity, and tables for the three counter categories.
//
// Built from every block this phone recorded for the session, not from what is
// still queued for upload. Uploaded blocks leave the queue, so a session resumed
// after a reload used to count only what was left: "1 blocks so far" for a
// session with two (UX-4 in the 2026-09-17 dev test).

import { CATEGORIES, type CategoryId } from "./categories.ts";
import { summarise, type Block } from "./tracker.ts";

export interface SummaryRow {
  categoryId: CategoryId;
  seconds: number;
  /** Share of observed time, 0 to 1. */
  share: number;
  /** Tables counted. Only meaningful for the counter categories. */
  taps: number;
}

export interface SessionSummary {
  observedSeconds: number;
  blocks: number;
  /** Activities that happened, in tile order. */
  rows: SummaryRow[];
}

/** One copy of each block, the later one winning: a block is saved open, then
    saved again when it closes. Ordered by block number. */
export function mergeBlocks(earlier: readonly Block[], later: readonly Block[]): Block[] {
  const byId = new Map<string, Block>();
  for (const b of earlier) byId.set(b.id, b);
  for (const b of later) byId.set(b.id, b);
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
}

/** Seconds since the first tap, never negative. Blocks run end to end with no
    gaps, so this equals the sum of the rows' seconds up to rounding. */
export function observedSeconds(blocks: readonly Block[], now: number): number {
  if (blocks.length === 0) return 0;
  const first = blocks.reduce((m, b) => Math.min(m, b.startedAt), Infinity);
  const last = blocks.reduce((m, b) => Math.max(m, b.endedAt ?? now), first);
  return Math.max(0, Math.floor((last - first) / 1000));
}

export function sessionSummary(blocks: readonly Block[], now: number): SessionSummary {
  const byCat = summarise({ blocks: [...blocks], nextSeq: 0 }, now);
  const total = [...byCat.values()].reduce((n, r) => n + r.seconds, 0);
  const rows = CATEGORIES
    .filter((c) => byCat.has(c.id))
    .map((c) => {
      const r = byCat.get(c.id)!;
      return { categoryId: c.id, seconds: r.seconds, share: total > 0 ? r.seconds / total : 0, taps: r.taps };
    });
  // The same total the tracker's top bar showed, so the end screen never
  // disagrees with the last reading by a few seconds of rounding.
  return { observedSeconds: observedSeconds(blocks, now), blocks: blocks.length, rows };
}

/** 0:07, 12:34, 1:02:09. Never negative. */
export function clock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}
